import { WmButton } from "@watermarkinsights/ripple-react";
import translate from "@/i18n/translate";
import ReportingYearTable from "./ReportingYearsTable";
import { useEffect, useReducer } from "react";
import { SettingsReportingYear } from "@/features/common/interfaces/Interface";
import { sendRequest } from "@/common/http";
import WidgetLoader from "@/features/common/elements/WidgetLoader";
import CreateReportingYearModal from "./CreateReportingYearModal";

interface ReportingYearState {
  reportingYears: SettingsReportingYear[] | null;
  showLoader: boolean;
  showReportingYearModalState: boolean;
  updateReportingYear: SettingsReportingYear | null;
}

interface Action {
  type: string;
  reportingYears: SettingsReportingYear[] | null;
  updateReportingYear: SettingsReportingYear | null;
}

const defaultAction: Action = {
  type: "",
  reportingYears: [],
  updateReportingYear: null
};

const reducer = (state: ReportingYearState, action: Action) => {
  switch (action.type) {
    case "setReportingYears":
      return {
        ...state,
        reportingYears: action.reportingYears,
        showLoader: false
      };
    case "toggleReportingYearModal":
      return {
        ...state,
        showReportingYearModalState: !state.showReportingYearModalState
      };

    case "updateReportingYear":
      return {
        ...state,
        updateReportingYear: action.updateReportingYear
      };

    default:
      return state;
  }
};

const initialState: ReportingYearState = {
  reportingYears: null,
  showLoader: true,
  showReportingYearModalState: false,
  updateReportingYear: null
};

function checkNoReportingYears(
  showLoader: boolean,
  reportingYears: SettingsReportingYear[] | null
) {
  return (
    showLoader === false &&
    reportingYears !== null &&
    reportingYears.length === 0
  );
}

function checkReportingYearsExist(
  showLoader: boolean,
  reportingYears: SettingsReportingYear[] | null
) {
  return showLoader === false && reportingYears && reportingYears.length > 0;
}

export function ReportingYears() {
  const [state, dispatch] = useReducer(reducer, initialState);

  function fetchReportingYears() {
    const applyResult = (result: SettingsReportingYear[]) => {
      dispatch({
        ...defaultAction,
        type: "setReportingYears",
        reportingYears: result
      });
    };
    sendRequest(
      { uri: "/sa/reporting_years?with_terms", camelize: true },
      applyResult
    );
  }

  useEffect(() => {
    fetchReportingYears();
  }, []);

  function toggleCreateReportingYearModal() {
    dispatch({
      ...defaultAction,
      type: "toggleReportingYearModal"
    });

    dispatch({
      ...defaultAction,
      type: "updateReportingYear",
      updateReportingYear: null
    });
  }

  function editReportingYearModal(reportingYear: SettingsReportingYear) {
    dispatch({
      ...defaultAction,
      type: "updateReportingYear",
      updateReportingYear: reportingYear
    });

    dispatch({
      ...defaultAction,
      type: "toggleReportingYearModal"
    });
  }
  return (
    <div className="main-content-body reporting-years-container">
      <div className="reporting-year-title">
        <div>{translate("app.settings.reportingYears")}</div>
        {checkReportingYearsExist(state.showLoader, state.reportingYears) && (
          <WmButton
            button-type="primary"
            id="add-new-reporting-year"
            className="create-reporting-year"
            onClick={toggleCreateReportingYearModal}
          >
            {translate("app.settings.addReportingYears")}
          </WmButton>
        )}
      </div>
      {state.showLoader && state.reportingYears === null && (
        <WidgetLoader loaderContainerClasses="loader" loaderClasses="content" />
      )}
      {checkReportingYearsExist(state.showLoader, state.reportingYears) && (
        <div id="reporting-year-table-wrapper">
          <ReportingYearTable
            reportingYears={state.reportingYears}
            editReportingYearModal={editReportingYearModal}
          />
        </div>
      )}

      {checkNoReportingYears(state.showLoader, state.reportingYears) && (
        <ReportingYearEmptyState
          toggleCreateReportingYearModal={toggleCreateReportingYearModal}
        />
      )}

      <CreateReportingYearModal
        openReportingYearModalState={state.showReportingYearModalState}
        closeReportingYearModal={toggleCreateReportingYearModal}
        reportingYears={state.reportingYears}
        fetchReportingYears={fetchReportingYears}
        updateReportingYear={state.updateReportingYear}
      />
    </div>
  );
}

interface ReportingYearEmptyStateProps {
  toggleCreateReportingYearModal: () => void;
}

function ReportingYearEmptyState({
  toggleCreateReportingYearModal
}: ReportingYearEmptyStateProps) {
  return (
    <div className="no-reporting-years-state">
      <table className="data-table -shadow ">
        <thead className="dataheader">
          <tr className="dataheaderrow">
            <th className="dataheadercell"></th>
          </tr>
        </thead>
        <tbody className="databody">
          <tr className="datarow">
            <td className="datacell">
              <div className="empty-msg">
                <span className="heading">
                  {translate("app.settings.noReportingYears")}
                </span>
                <div className="-message">
                  {translate("app.settings.noReportingYearsText")}
                </div>
                <div className="add-reporting-year">
                  <WmButton
                    button-type="primary"
                    id="add-new-reporting-year"
                    onClick={toggleCreateReportingYearModal}
                  >
                    {translate("app.settings.addReportingYears")}
                  </WmButton>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
